import CheckoutState, { ISubscriptionPlanQuery } from 'Types/_Store/Checkout'
import { ActionTree, MutationTree, GetterTree } from 'vuex'
import { getEstimate, getPlans, getPeriods, paySubscription, checkPayment, getPlanStatus, getPixInfo, getBoletoInfo } from 'Api/subscriptionApi'
import { StoreState, SubscriptionPeriod, BillingInfo, CreditCard, ApiBillingInfo, PaymentMethod } from 'Types'
import { subscriptionSignalR } from 'Services/signalR'
import subscriptionErrorTypes from 'Enums/subscriptionErrorTypes'
import appPlanList, { AppPlan } from 'Services/appPlanList'
import { parsePeriod, parsePlanListNew, updatePeriodList, parsePlanNew, updatePeriodListArray} from 'Parsers/subscriptionParser'
import { parseBillingInfo } from 'Parsers/billingParser'
import { getBillingData } from 'Api/billingApi'
import { HubConnection } from '@aspnet/signalr'
import brazilStates from 'Enums/brazilStates'
import planContainerTypes from 'Enums/planContainerTypes'
import { checkout } from 'routes'
import { notifyError } from 'Services/vueNotifier'

const state: CheckoutState = {
  connectionId: null,

  requesting: {
    plan: false,
    billing: false,
    periodList: false,
    planList: false,
    realtime: false,
    subscription: false,
    pixInfo: false,
    boletoInfo: false
  },

  plan: {
    id: 0,
    period: {
      id: 0,
      name: '',
      suffix: '',
      discount: 0,
      price: '',
    },
    container: 0,
    name: '',
    price: 0,
    priority: '',
    discount: '',
    bonus: '',
    category: '',
    city: '',
    position: '',
    support: false,
  },
  billing: null,
  card: null,
  boleto: false,
  pix: false,

  billingRegion: {
    cityName: '',
    stateName: '',
  },

  periodList: [],
  planList: [],
  pixInfo: {
    amountFormat: '',
    copyPasteCode: '',
    expirationDate: '',
    expirationDateFormat: '',
    qrCode: '',
    status: 0,
    statusDescription: '',
  },
  boletoInfo: {
    status: 0,
    statusDescription: '',
    plan: '',
    barCode: '',
    downloadUrl: '',
    expirationDate: '',
    expirationDateFormat: '',
    amountFormat: ''
  },

  subscribed: false,
  upgrade: false,
}

const getters: GetterTree<CheckoutState, StoreState> = {
  ready: state => {
    return state.plan !== null && state.requesting.plan === false
  },
  valid: state => {
    const validCard = (state.card !== null && typeof state.card.id === 'number')
    const validPlan = (state.plan !== null && typeof state.plan.id === 'number')
    const validPeriod = (validPlan && typeof state.plan.period.id === 'number')

    return (validCard && validPlan && validPeriod) || state.boleto || state.pix
  }
}

const mutations: MutationTree<CheckoutState> = {
  request_start(state, type) {
    state.requesting[type] = true
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  request_failure(state, type) {
    state.requesting[type] = false
  },

  set_connection_id(state, connectionId) {
    state.connectionId = connectionId
  },

  set_plan(state, plan) {
    state.plan = plan
  },

  set_plan_list(state, planList) {
    state.planList = planList
  },

  set_period_list(state, periodList) {
    state.periodList = periodList
  },

  set_billing(state, billing) {
    state.billing = billing
  },

  set_billing_region(state, billingRegion) {
    state.billingRegion = billingRegion
  },

  set_card(state, card) {
    state.card = card
  },

  set_boleto(state, isBoleto) {
    state.boleto = isBoleto
  },

  set_pix(state, isPix) {
    state.pix = isPix
  },

  set_subscribed(state, subscribed) {
    state.subscribed = subscribed
  },

  set_upgrade(state, upgrade) {
    state.upgrade = upgrade
  },

  set_pix_info(state, data) {
    state.pixInfo = data
  },

  set_boleto_info(state, data) {
    state.boletoInfo = data
  },

  set_installments(state, data) {
    state.installments = data
  }
}

const actions: ActionTree<CheckoutState, StoreState> = {
  getBillingRegion({ commit }, data: ApiBillingInfo) {
    const { city: cityName } = data
    const stateName = brazilStates.getDescriptionByValue(data.stateId)

    commit('set_billing_region', { cityName, stateName })
  },

  async startConnection({ commit }) {
    commit('request_start', 'realtime')

    try {
      const connection = await subscriptionSignalR()
      const connectionId = await connection.invoke('getConnectionId')

      commit('set_connection_id', connectionId)
      commit('request_success', 'realtime')

      return connection
    } catch (err) {
      commit('request_failure', 'realtime')

      throw err
    }
  },

  async getSubscriptionPlan({ commit, state, dispatch }, query: ISubscriptionPlanQuery = null): Promise<AppPlan> {
    commit('request_start', 'plan')

    const isSignupCheckout = (query === null)

    try {
      const { data } = isSignupCheckout
        ? await getEstimate(state.connectionId)
        : await getEstimate(state.connectionId, query.planId, query.periodId)

      const [selectedPlan, selectedPeriod] = await Promise.all([
        dispatch('getPlan', {planId: data.planId, periodId: data.periodId},),
        dispatch('getPeriod', data.periodId)
      ]) as [SubscriptionPeriod, AppPlan]

      if (selectedPlan === null)
        throw { response: { data: ["Não foi possível encontrar o plano solicitado"] } }

      if (selectedPeriod === null)
        throw { response: { data: ["Não foi possível encontrar o período solicitado"] } }

      const estimatePlan = selectedPlan.withPeriod(selectedPeriod)

      commit('set_plan', estimatePlan)
      commit('request_success', 'plan')

      return estimatePlan
    } catch (err) {
      if (err.response && err.response.data && err.response.data.length && err.response.data.includes('Plano não foi encontrado')) {
        throw subscriptionErrorTypes.upgrade
      }

      throw query === null
        ? subscriptionErrorTypes.signup
        : subscriptionErrorTypes.upgrade
    }
  },

  async getPlan(_, planData): Promise<AppPlan> {
    const {planId, periodId } = planData
    const { data } = await getPlans()
    const { items: planList } = data

    const apiPlan = planList.find(plan => plan.id === planId)

    const updatedPlans = updatePeriodList(periodId, apiPlan)

    const appPlan = parsePlanNew(updatedPlans)


    return appPlan || null
  },

  async getPlanBlacklist(_) {
    const unavailablePlans = [
      planContainerTypes.demand,
      planContainerTypes.diamond
    ]

    const { data: statusData } = await getPlanStatus()

    const blockedPlans = planContainerTypes
      .toList()
      .map(plan => plan.value)
      .filter(container => container > statusData.container)

    return [
      ...unavailablePlans,
      ...blockedPlans
    ]
  },

  async getPlanList({ commit, dispatch, state }): Promise<AppPlan[]> {
    commit('request_start', 'planList')

    try {
      const planBlacklist = await dispatch('getPlanBlacklist')
      const { data } = await getPlans()
      const { items: planList } = data

      const planListFiltred = planList.filter(plan => !planBlacklist.includes(plan.container))
      const updatedPlans = updatePeriodListArray(state.plan.period.id, planListFiltred)

      let parsedList = parsePlanListNew(updatedPlans)

      parsedList.forEach(plan => {

        plan = plan
          .withPeriod(state.plan.period)
      })

      commit('set_plan_list', parsedList)

      commit('request_success', 'planList')

      return parsedList
    } catch (err) {
      commit('request_failure', 'planList')

      throw err
    }
  },

  async getPeriod(_, periodId: number): Promise<SubscriptionPeriod> {
    const { data } = await getPeriods()

    const apiPeriod = data.find(period => period.id === periodId)
    const appPeriod = parsePeriod(apiPeriod)

    return appPeriod || null
  },

  async getPeriodList({ commit, dispatch }): Promise<SubscriptionPeriod[]> {
    commit('request_start', 'periodList')

    const blacklistFilter = period => [''].includes(period.name) === false

    try {
      const { data } = await getPeriods()
      const periodList = data.map(parsePeriod).filter(blacklistFilter)
      const priceList = await Promise.all(periodList.map(period => dispatch('getPriceByPeriod', period.id)))

      periodList.forEach(period => {
        const { price } = priceList.find(item => item.periodId === period.id)

        period['price'] = price
      })

      commit('set_period_list', periodList)
      commit('request_success', 'periodList')

      return periodList
    } catch (err) {
      commit('request_failure', 'periodList')

      throw err
    }
  },

  async getPriceByPlan({ state }, planId: number): Promise<object> {
    const { data } = await getEstimate(state.connectionId, planId, state.plan.period.id)
    const { amount: price } = data

    return { planId, price }
  },

  async getPriceByPeriod({ state }, periodId: number): Promise<object> {
    const { data } = await getEstimate(state.connectionId, state.plan.id, periodId)
    const { amount: price } = data

    return { periodId, price }
  },

  async getBilling({ commit, dispatch }): Promise<BillingInfo> {
    commit('request_start', 'billing')

    try {
      const { data } = await getBillingData()

      if (!data || data === '')
        throw { response: { data: ["Não existem dados de faturamento para essa empresa"] } }

      const billing = parseBillingInfo(data)

      dispatch('getBillingRegion', data)

      commit('set_billing', billing)
      commit('request_success', 'billing')

      return billing
    } catch (err) {
      commit('request_failure', 'billing')

      throw err
    }
  },

  async paySubscription({ commit, state }, connection: HubConnection) {
    commit('request_start', 'subscription')
    try {
      const result = await paySubscription({
        planId:  state.plan.id,
        periodId: state.plan.period.id,
        creditCardId: !state.boleto && !state.pix ? state.card.id : null,
        paymentMethod: state.boleto ? PaymentMethod.boleto : state.pix ? PaymentMethod.pix : PaymentMethod.credito,
        installments: state.installments
      })

      if (state.pix) {
        var urlActive = window.location.href;
        var currentParams = urlActive.split('?')[1];

        if (currentParams) {
          var newUrlWithParams = `${checkout.pix}?${currentParams}`;
          location.assign(newUrlWithParams);
        } else {
          location.assign(checkout.pix);
        }
        return
      }

      if (state.boleto) {
        var urlActive = window.location.href;
        var currentParams = urlActive.split('?')[1];

        if (currentParams) {
          var newUrlWithParams = `${checkout.boleto}?${currentParams}`;
          location.assign(newUrlWithParams);
        } else {
          location.assign(checkout.boleto);
        }
        return
        return
      }

      await checkPayment(connection)

      commit('set_subscribed', true)
      commit('request_success', 'subscription')

      location.reload()

      return
    } catch (err) {
      commit('request_failure', 'subscription')
      notifyError(this, err)
      throw err
    }
  },

  updateCard({ commit }, card: CreditCard) {
    commit('set_boleto', false)
    commit('set_card', card)
    commit('set_pix', false)
  },

  updateBoleto({ commit }, isBoleto: boolean) {
    commit('set_boleto', isBoleto)
    commit('set_card', null)
    commit('set_pix', false)

  },

  updatePix({ commit }, isPix: boolean) {
    commit('set_boleto', false)
    commit('set_card', null)
    commit('set_pix', isPix)

  },

  enableCheckoutUpgrade({ commit }) {
    commit('set_upgrade', true)
  },

  async getPixInfo({ commit, state}) {
    commit('request_start', 'pixInfo')

    try {
      commit('request_success', 'pixInfo')

      const { data } = await getPixInfo()

      commit('set_pix_info', data)

      return data
    } catch (error) {
      commit('request_failure', 'pixInfo')

      throw error

    }
  },

  async getBoletoInfo({ commit }) {
    commit('request_start', 'boletoInfo')

    try {
      commit('request_success', 'boletoInfo')

      const { data } = await getBoletoInfo()

      commit('set_boleto_info', data)

      return data
    } catch (error) {
      commit('request_failure', 'boletoInfo')

      throw error

    }
  },

  updateInstallments({ commit }, value: number) {
    commit('set_installments', value)

  },
}

export default {
  namespaced: true,

  state,
  getters,
  actions,
  mutations
}